import React, { Fragment, useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import { useHistory } from "react-router";
import { KNOCK_WORKFLOW, ROLE } from "@highnote/server/src/core/entities";
import {
  getEntityShareKeys,
  getEntitySubscribers,
  isAllowed,
  isSpacePrivateInboxEnabled,
  PERMISSION,
} from "@highnote/server/src/core/shared-util";
import { useJoinEntityButton } from "App/common/AuthButtons";
import { Breadcrumbs } from "App/common/Breadcrumbs";
import { ReactComponent as ChatSVG } from "App/common/icons-v2/chat.svg";
import { ReactComponent as TrackCommentSVG } from "App/common/icons-v2/comment-stack-line.svg";
import { ReactComponent as LinkSVG } from "App/common/icons-v2/link.svg";
import { ReactComponent as EditSVG } from "App/common/icons-v2/edit.svg";
import { useNotificationsContext } from "App/common/useNotifications";
import { useSpaceContext } from "App/common/useSpace";
import { useViewport } from "App/common/useViewport";
import { useAuth } from "App/components/Auth";
import { useUrlContext } from "../useUrlContext";
import { TrackDownloadButton } from "App/components/EntityTable/EntityShortcut";
import { Navigation, NavigationToolbar } from "App/components/Navigation";
import { useShareDialog } from "App/components/ShareDialog";
import { setSidebarUrl, SIDEBAR_ID } from "App/components/Sidebar";
import { SpaceMemberPreview } from "App/components/SpaceTools";
import { useCreatedTracks } from "App/components/useEntities/useCreatedTracks";
import { useTrack, useTrackFiles } from "App/components/useEntities/useTrack";
import { Button, BUTTON_SIZE, BUTTON_THEME } from "App/core/Button";
import { PrivateInboxUploadSuccessfullyDialog } from "./PrivateInboxUploadSuccesfullyDialog";
import { useMobileAppParams } from "../useMobileAppParams";
import "./SpaceNavigation.scss";
import { UnreadDotV2 } from "App/common/UnreadDotV2";
import { TrackCommentSidebar } from "App/components/Sidebar/TrackCommentSidebar";
import { DefaultSpaceActions } from "App/common/SpaceActions";
import { DefaultTrackActions } from "App/components/useEntities/useLibraryTracks/TrackActions";
import { useGlobalSpaces } from "App/store/spaces/useGlobalSpaces";
import { SHARE_KEY_PREVIEW_PARAM } from "App/modules/urls";
import { DownloadAppButton } from "App/common/DownloadAppButton";
import { useMembershipFlags } from "App/components/MembershipFlagsContext";
import { useSpaceAccessMembers } from "App/components/useEntities/useSpaceAccessMembers";

const SpaceDetailsButton = () => {
  const { space } = useSpaceContext();
  return <DefaultSpaceActions space={space} />;
};

const TrackDetailsButton = () => {
  const { spaceId } = useSpaceContext();
  const { track } = useTrack();
  // The track page IS scoped to the route's space.
  return <DefaultTrackActions track={track} viewingSpaceId={spaceId} />;
};

const SpaceChatToolbarButton = () => {
  const history = useHistory();
  const { space } = useSpaceContext();
  const { notifications } = useNotificationsContext();
  const { isMobile } = useViewport();

  const unreads = useMemo(
    () =>
      notifications.filter(
        (n) =>
          n.source.key === KNOCK_WORKFLOW.CHAT_COMMENT_ADDED &&
          n.data.spaceId === space?.id &&
          !n.read_at,
      ),
    [space?.id, notifications],
  );

  return (
    <span className="space-navigation-chat">
      {unreads.length > 0 && <UnreadDotV2 position="left" />}
      <Button
        className="space-chat-button"
        size={BUTTON_SIZE.SMALL}
        theme={isMobile ? BUTTON_THEME.SECONDARY : BUTTON_THEME.FLAT_BLACK}
        onClick={() => setSidebarUrl(SIDEBAR_ID.SPACE_CHAT, history)}
      >
        <ChatSVG />
        <span className="label">Chat</span>
      </Button>
    </span>
  );
};

const TrackCommentsToolbarButton = ({
  onOpenTrackComments,
}: {
  onOpenTrackComments: () => void;
}) => {
  return (
    <Button
      className="chat-button track-comment-toolbar-button svg-stroke-only-container"
      size={BUTTON_SIZE.LARGE}
      theme={BUTTON_THEME.ICON}
      onClick={onOpenTrackComments}
      aria-label="Toggle Track Comments"
    >
      <TrackCommentSVG />
    </Button>
  );
};

type Props = {
  hasChatButton?: boolean;
  onOpenTrackComments?: () => void;
};

const SpaceNavigationToolbar = ({
  hasChatButton,
  onOpenTrackComments,
}: Props) => {
  const history = useHistory();
  const { showShareDialog } = useMobileAppParams();
  const { space, spaceId } = useSpaceContext();
  const { viewAsShareKeyUser, shareKey } = useUrlContext();
  const { track, trackArtworkUrl } = useTrack();
  const { openShareDialog, renderShareDialog } = useShareDialog();
  /*
   * isAllowed from useAuth (renamed to isCurrentUserAllowed) should only be used
   * for the currently logged in user. To check permissions for other user IDs
   * (like share keys), use isAllowed from @highnote/server/src/core/shared-util.
   *
   * For share key preview mode, we hide UI elements that the share key user
   * wouldn't be able to see, using the viewAsShareKeyUser ternary pattern below.
   */
  const { user, isAllowed: isCurrentUserAllowed, isPublicView } = useAuth();
  const { createdTracks } = useCreatedTracks();
  const isLoggedIn = !!user;

  const { isMobile } = useViewport();
  const subscribers = getEntitySubscribers(space);
  const isMember = subscribers.includes(user?.id);
  const isOwner = space.createdBy === user?.id;
  const isInboxEnabled = isSpacePrivateInboxEnabled(space);

  // Below M, drop the member-only chrome (facepile / share status, link icon,
  // download, 3-dot actions) on both the space-home and track headers — we don't
  // want these actions taken on mobile web. Members instead get a "Use the App"
  // CTA on the track page (added at the end).
  const hideMemberActions = isMobile;
  const joinSpaceButton = useJoinEntityButton(space, "Space");

  const [showUploadSuccessDialog, setShowUploadSuccessDialog] =
    useState<boolean>(false);

  const canDownload = viewAsShareKeyUser
    ? isAllowed(PERMISSION.TO_DOWNLOAD_TRACK_IN_SPACE, shareKey, {
        track,
        space,
      })
    : isCurrentUserAllowed(PERMISSION.TO_DOWNLOAD_TRACK_IN_SPACE, {
        track,
        space,
      });

  const handleOpenShareDialog = () => openShareDialog(space);

  useEffect(() => {
    if (showShareDialog && space) {
      handleOpenShareDialog();
    }
  }, [showShareDialog, space]);

  /*
   * The share-link read: `isSpaceShared` is the third of the three sites
   * that derive share state from `space.rolesV2[shareKey]`.
   */
  const { membersV2Enabled } = useMembershipFlags();
  const canManageSpace = isCurrentUserAllowed(PERMISSION.TO_MANAGE_SPACE, {
    space,
  });
  const access = useSpaceAccessMembers({
    spaceId: space?.id,
    canManage: canManageSpace,
    enabled: membersV2Enabled,
  });
  const useAccessDocs =
    membersV2Enabled && canManageSpace && !access.loading && !access.error;
  const accessShareRoles = access.shareMember?.effectiveRoles;

  const {
    isSpaceShared,
    hasMembersSpaceNotShared,
    hasMembersSpaceShared,
    noMembersSpaceNotShared,
  } = useMemo(() => {
    // Get all the valid share keys (must have roles attached).
    const entityRoles = space?.rolesV2 || {};
    const [shareKey] = getEntityShareKeys(space);
    const shareRoles = useAccessDocs
      ? accessShareRoles || []
      : entityRoles[shareKey] || [];
    const isSpaceShared = shareRoles.includes(ROLE.VIEW);

    /*
     * Warning: `hasMembers` deliberately stays on the legacy `rolesV2` map.
     * This is a recorded deviation from the migration plan, which assigns
     * the whole derivation to the share-link step. The plan's share-link
     * section assigns the `hasMembers` half to the roster step, but the
     * roster section covers the "N people" counters, the avatar stacks,
     * and `isEntityOwner`, and never gives a member-document derivation
     * for this predicate.
     *
     * The obvious substitute is `space.memberCount`, and it is wrong.
     * This expression reads the raw `rolesV2` map, which does not contain
     * the creator unless somebody granted them a role explicitly. So a
     * solo space (a space with only its creator) has `hasMembers === false`
     * and renders the literal string
     * "Only you". `memberCount` counts direct members plus the creator, so
     * it is at least 1 on every space that exists. With `memberCount > 0`,
     * every solo space renders an avatar stack instead of "Only you".
     * `memberCount > 1` is a guess at the same fact by a different route,
     * and the project's default rule forbids a guessed behavior change
     * inside a data migration.
     *
     * This is correct as it stands while the legacy fields are still
     * written: the migration's dual-write contract guarantees that the
     * server still writes `rolesV2`. We flagged this deviation for the
     * spec owner.
     */
    const memberKeys = Object.keys(entityRoles).filter(
      (key) => !key.startsWith("share_"),
    );
    const hasMembers = memberKeys.some(
      (key) => Array.isArray(entityRoles[key]) && entityRoles[key].length > 0,
    );

    const hasMembersSpaceShared = hasMembers && isSpaceShared;
    const hasMembersSpaceNotShared = hasMembers && !isSpaceShared;
    const noMembersSpaceNotShared = !hasMembers && !isSpaceShared;

    return {
      isSpaceShared,
      hasMembersSpaceNotShared,
      hasMembersSpaceShared,
      noMembersSpaceNotShared,
    };
  }, [space, useAccessDocs, accessShareRoles]);

  const toolbarItems = useMemo(() => {
    const items: React.ReactElement[] = [];

    if (isMember && !viewAsShareKeyUser && !hideMemberActions) {
      if (hasMembersSpaceNotShared || hasMembersSpaceShared) {
        items.push(<SpaceMemberPreview onClick={handleOpenShareDialog} />);
      } else {
        items.push(
          <div className="share-label">
            {noMembersSpaceNotShared ? "Only you" : "Link on"}
          </div>,
        );
      }
    } else if (isInboxEnabled && createdTracks.length > 1 && !isMember) {
      // Only appear to guests if they have new uploads
      items.push(
        <Fragment>
          <Button
            theme={BUTTON_THEME.CTA}
            size={BUTTON_SIZE.SMALL}
            onClick={() => setShowUploadSuccessDialog(true)}
          >
            Done
          </Button>
          <PrivateInboxUploadSuccessfullyDialog
            isOpen={showUploadSuccessDialog}
            redirectUrl={space.privateInboxRedirectUrl}
            onClose={() => setShowUploadSuccessDialog(false)}
            spaceOwner={space.createdBy}
          />
        </Fragment>,
      );

      return items;
    }

    if (isMember && !hideMemberActions) {
      items.push(
        <>
          <Button
            className="share"
            size={BUTTON_SIZE.LARGE}
            theme={BUTTON_THEME.ICON}
            onClick={handleOpenShareDialog}
            aria-label={`Open Share Space Details - ${isSpaceShared ? "Share Link enabled" : "Share Link disabled"}`}
          >
            <LinkSVG />
            <div
              className={classNames("space-enabled-indicator", {
                ["space-disabled-indicator"]: !isSpaceShared,
              })}
            />
          </Button>
        </>,
      );
    }

    // Download button should only show on Track pages
    if (canDownload && !!track && !hideMemberActions) {
      items.push(
        <TrackDownloadButton
          className="track-navigation-download-button"
          track={track}
          trackArtworkUrl={trackArtworkUrl}
          showDownloadSpace
          viewingSpaceId={spaceId}
        />,
      );
    }

    if (
      hasChatButton &&
      (isCurrentUserAllowed(PERMISSION.TO_COMMENT_IN_SPACE, { space }) ||
        !isPublicView(space))
    ) {
      items.push(<SpaceChatToolbarButton />);
    }

    if (
      onOpenTrackComments &&
      // Commenting is a member action — never show the toggle to logged-out
      // users on the track page.
      isLoggedIn &&
      !viewAsShareKeyUser &&
      // TODO: What permission should be used here?
      (isCurrentUserAllowed(PERMISSION.TO_COMMENT_IN_SPACE, { space }) ||
        !isPublicView(space))
    ) {
      items.push(
        <TrackCommentsToolbarButton
          onOpenTrackComments={onOpenTrackComments}
        />,
      );
    }

    if (isOwner && viewAsShareKeyUser) {
      // If a user is viewing the track page in share key preview mode (currently only accessible via the TrackStories flow),
      // display a CTA in the toolbar to allow them to exit preview mode.
      items.push(
        <Button
          theme={BUTTON_THEME.MONETIZATION}
          size={BUTTON_SIZE.SMALL}
          startIcon={<EditSVG />}
          onClick={() => {
            const query = new URLSearchParams(location.search);
            query.delete(SHARE_KEY_PREVIEW_PARAM);
            history.replace({
              pathname: location.pathname,
              search: query.toString(),
            });
          }}
        >
          Edit
        </Button>,
      );
    }

    if (isMember && !viewAsShareKeyUser && !hideMemberActions) {
      if (track) {
        items.push(<TrackDetailsButton />);
      } else {
        items.push(<SpaceDetailsButton />);
      }
    }

    // Above M, non-members get the web "Join Space" CTA. Below M we send
    // everyone to the app first (handled below), so this is desktop-only.
    if (!isMember && !isInboxEnabled && !isMobile) {
      items.push(joinSpaceButton);
    }

    // Below M (space or track page): download-the-app CTA for everyone. Members
    // have their web actions stripped above; non-members are pushed to the app
    // before a web join.
    if (isMobile) {
      items.push(<DownloadAppButton size={BUTTON_SIZE.SMALL} />);
    }
    return items;
  }, [
    isLoggedIn,
    space,
    joinSpaceButton,
    isMember,
    hideMemberActions,
    track,
    isMobile,
  ]);

  return (
    <>
      <NavigationToolbar items={toolbarItems} />
      {renderShareDialog}
    </>
  );
};

// This already redirects to the library page on desktop, and to the home page on mobile.
const HOME_PATH = "/";

export const SpaceHomeNavigation = () => {
  const history = useHistory();
  const { space } = useSpaceContext();
  const { user } = useAuth();
  const isLoggedIn = !!user;
  const subscribers = getEntitySubscribers(space);
  const isMember = subscribers.includes(user?.id);
  const { isMobile } = useViewport();
  const { globalSpaces, manageGlobalSingleSpaceWatcher } = useGlobalSpaces();

  const hasParentSpace =
    space?.spaceId && globalSpaces.spaces.get(space.spaceId);

  // The space home is a top-level destination, so below M nobody needs the
  // breadcrumb trail or back button — the logo stands in for both. (Contrast the
  // track nav, where a member keeps the back button to climb out of a track.)
  // Above M, still strip the header for logged-out users.
  const showBareHeader = isMobile || !isLoggedIn;

  useEffect(() => {
    if (space?.spaceId) {
      // Subscribe to space watcher to get space changes and data
      manageGlobalSingleSpaceWatcher({
        action: "attach",
        componentId: "space-navigation",
        spaceId: space.spaceId,
      });

      return () => {
        manageGlobalSingleSpaceWatcher({
          action: "detach",
          componentId: "space-navigation",
          spaceId: space.spaceId,
        });
      };
    }
  }, [space?.spaceId]);

  return (
    <Navigation
      className="space-navigation space-home-navigation"
      showIndent
      showLogo={showBareHeader}
      // No back button below M — the space home is a top-level destination, so
      // there's nowhere to go "back" to on mobile. Above M, hide it only when a
      // logged-out user has no parent space to return to.
      hideBackButton={isMobile || (!isLoggedIn && !hasParentSpace)}
      onGoBack={() => {
        if (hasParentSpace) {
          // Navigate to the parent space if it exists and the user has access to it
          history.push(`/space/${space.spaceId}`);
        } else {
          // Navigate to the home page if the user does not have access to the parent space
          history.push(HOME_PATH);
        }
      }}
    >
      {!showBareHeader && (
        <Breadcrumbs
          componentId="breadcrumbs-space-home"
          activeSpace={space}
          hideLibrary={!isMember}
        />
      )}
      <SpaceNavigationToolbar hasChatButton={Boolean(space?.chatEnabled)} />
    </Navigation>
  );
};

export const SpaceTrackNavigation = () => {
  const [openCommentSidebar, setOpenCommentSidebar] = useState(false);
  const { space, spaceId } = useSpaceContext();
  const { viewAsShareKeyUser } = useUrlContext();
  const { track } = useTrack();
  // The track page is scoped to the route's space.
  const { currentTrackPageVersion } = useTrackFiles(track, spaceId);
  const history = useHistory();
  const { user } = useAuth();
  const isLoggedIn = !!user;
  const { isMobile } = useViewport();

  const { globalSpaces, manageGlobalSingleSpaceWatcher } = useGlobalSpaces();

  const parentSpace =
    space || (track?.spaceId && globalSpaces.spaces.get(track.spaceId));
  const subscribers = getEntitySubscribers(parentSpace);
  const isMember = subscribers.includes(user?.id);

  // A guest on mobile gets a bare header: logo only, no back button. Same rule
  // as the space-home nav (SpaceHomeNavigation).
  const isGuestMobile = !isLoggedIn && isMobile;

  // The full breadcrumb trail is too wide for mobile, so it's hidden below M for
  // everyone. A logged-in member still keeps the back button to climb out of a
  // track — they just don't get the whole trail.
  const hideBreadcrumb = isMobile;

  useEffect(() => {
    if (!space?.id && track?.spaceId) {
      // Subscribe to space watcher to get space changes and data
      manageGlobalSingleSpaceWatcher({
        action: "attach",
        componentId: "track-navigation",
        spaceId: track.spaceId,
      });

      return () => {
        manageGlobalSingleSpaceWatcher({
          action: "detach",
          componentId: "track-navigation",
          spaceId: track.spaceId,
        });
      };
    }
  }, [track?.spaceId, space?.id]);

  return (
    <Navigation
      className="space-navigation space-track-navigation"
      showLogo={!isLoggedIn}
      showIndent
      hideBackButton={isGuestMobile}
      onGoBack={() => {
        if (parentSpace) {
          // Navigate to the parent space if it exists and the user has access to it
          history.push(`/space/${parentSpace.id}`);
        } else {
          // Navigate to the home page if the user does not have access to the parent space
          history.push(HOME_PATH);
        }
      }}
    >
      {!hideBreadcrumb && (
        <Breadcrumbs
          activeSpace={parentSpace}
          activeTrack={track}
          activeTrackVersion={currentTrackPageVersion}
          hideLibrary={!isMember || viewAsShareKeyUser}
          componentId="breadcrumbs-space-track"
        />
      )}
      <SpaceNavigationToolbar
        onOpenTrackComments={() => setOpenCommentSidebar(true)}
      />
      <TrackCommentSidebar
        isSidebarOpen={openCommentSidebar}
        onCloseSidebar={() => setOpenCommentSidebar(false)}
      />
    </Navigation>
  );
};
